import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome ---
import {
    faMapMarkedAlt, faDraftingCompass, faBuilding, faCogs, faCube,
    faProjectDiagram, faClipboardCheck, faLeaf, faGlobeAmericas, faRulerCombined,
    faProcedures, faUsersCog, faChartLine, faStethoscope, faSitemap, faObjectGroup,
    faPencilRuler, faTasks, faSyncAlt, faClipboardList, faLightbulb, faQuoteLeft,
    // Added icons based on new content analysis
    faShieldAlt, faTruck, faBullhorn, faLaptop, faUsers, faDatabase, faFolderOpen,
    faChalkboardTeacher, faHandshake, faCheckCircle, faChartBar
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    // Ensure parent exists and children can be accessed before calculating index
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        // Target prefixed class
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles ---
    const titleObserverOptions = { threshold: 0.3 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions);
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions);
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions);
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Added for new section
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // Added for Methodology
    const [title6Ref, title6Visible] = useIntersectionObserverTitle(titleObserverOptions); // Added for Results

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Keeping original hero image, but can be changed if needed
    const heroBgImage = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80';

  return (
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Hospital Management Consulting</h1>
            <p className="cliniqor-sub-heading">
              Transforming Healthcare Institutions Through Strategic, Data-Driven Solutions
            </p>
            <p className="cliniqor-intro-text">
              At Cliniqor, we specialize in delivering comprehensive hospital management consulting solutions designed to elevate operational efficiency, drive financial performance, and ensure long-term sustainability in healthcare institutions. With an unwavering focus on excellence, innovation, and compliance, we empower hospitals, clinics, and healthcare investors to overcome challenges and unlock their full potential.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      {/* --- REMOVED OLD HIGHLIGHTS SECTION as it doesn't map well to new content --- */}
      {/* <section className="cliniqor-highlights-section"> ... </section> */}

      <main>
        {/* --- INTRO SECTION --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure for layout consistency */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>Our Consulting Approach</h3>
                     <p>Our consulting approach is rooted in industry best practices, in-depth analytics, and personalized strategies that address the evolving dynamics of the healthcare ecosystem.</p>
                     <p>Whether you're launching a new healthcare facility, expanding existing infrastructure, or seeking performance improvement in daily operations, Cliniqor offers end-to-end guidance from planning through execution.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
                 {/* Removed old quote box, can be replaced if needed */}
                 {/* <div className="cliniqor-intro-quote-box cliniqor-fade-in-up"> ... </div> */}
             </div>
          </div>
        </section>

        {/* --- CORE SERVICES / FEATURES SECTION --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            {/* Apply prefixed visible class based on observer state */}
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Comprehensive Consulting Solutions</h2>
              <p>End-to-end guidance covering strategic planning, financial viability, operational efficiency, and growth.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained */}
              {/* --- NEW FEATURE CARDS (Mapped from numbered sections 1-9) --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div>
                    <h4>1. Market & Financial Feasibility Analysis</h4>
                    <p>Building a Strong Foundation for Sustainable Healthcare Ventures.</p>
                    {/* Detailed sub-points omitted for brevity in card view */}
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div> {/* Using faTasks for optimization */}
                    <h4>2. Revenue Optimization & Cost Control</h4>
                    <p>Enhancing Financial Performance Without Compromising Clinical Excellence.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                    <h4>3. Hospital Audit & Facility Assessment</h4>
                    <p>Uncovering Operational Gaps to Achieve Excellence in Performance & Compliance.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faProjectDiagram} /></div> {/* Using faProjectDiagram for expansion */}
                    <h4>4. Expansion Planning & Operational Enhancement</h4>
                    <p>Designing Scalable Solutions for Long-Term Growth and Institutional Resilience.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptop} /></div> {/* Using faLaptop for tech */}
                    <h4>5. Technology-Driven Healthcare Solutions</h4>
                    <p>Integrating cutting-edge technologies for superior patient care and efficiency.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <h4>6. Workforce Optimization & HR Consulting</h4>
                    <p>Enhancing staff performance, recruitment, satisfaction, and alignment.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div>
                    <h4>7. Risk Management and Compliance Advisory</h4>
                    <p>Mitigating risks and ensuring adherence to the latest healthcare regulations.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTruck} /></div> {/* Using faTruck for supply chain */}
                    <h4>8. Healthcare Supply Chain Optimization</h4>
                    <p>Ensuring efficient management of medical supplies and equipment.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up"> {/* Added 9th card */}
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLeaf} /></div>
                    <h4>9. Sustainability and Green Healthcare Design</h4>
                    <p>Promoting eco-friendly operations and reducing environmental impact.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- WHY CHOOSE US SECTION --- */}
         <section className="cliniqor-section cliniqor-section-bg-dark cliniqor-why-choose-us-section">
             <div className="cliniqor-container">
                 <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
                    {/* --- NEW SECTION TITLE --- */}
                    <h2>Why Cliniqor?</h2>
                    <p>Your Trusted Partner in Healthcare Transformation</p>
                    {/* --- END NEW SECTION TITLE --- */}
                </div>
                <div className="cliniqor-why-choose-us-grid"> {/* Grid structure maintained */}
                     {/* --- NEW WHY CHOOSE US ITEMS --- */}
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faUsersCog} /></span> <span className="cliniqor-text"><strong>Multi-Disciplinary Expertise:</strong> Architects, planners, strategists, clinical experts, financial analysts.</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faHandshake} /></span> <span className="cliniqor-text"><strong>Client-Centric Approach:</strong> Tailored recommendations for your unique vision, mission, and capabilities.</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faSitemap} /></span> <span className="cliniqor-text"><strong>Holistic Advisory Model:</strong> Full-spectrum support from ideation to execution and review.</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faChartBar} /></span> <span className="cliniqor-text"><strong>Data-Driven Decisions:</strong> Rooted in analytics, research, and benchmarks for measurable results.</span>
                     </div>
                     <div className="cliniqor-why-choose-us-item cliniqor-fade-in-up">
                         <span className="cliniqor-icon"><FontAwesomeIcon icon={faCheckCircle} /></span> <span className="cliniqor-text"><strong>Track Record of Excellence:</strong> Impactful solutions for clinics to multi-specialty hospitals.</span>
                     </div>
                     {/* Removed one item to match the 5 points in the new content */}
                     {/* --- END NEW WHY CHOOSE US ITEMS --- */}
                </div>
             </div>
         </section>

        {/* --- OPERATIONAL EXCELLENCE / SUPPORT SERVICES SECTION --- */}
        {/* Replaced old "Detailed Planning" with the second block of numbered points */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Operational Excellence & Support</h2>
               <p>Enhancing daily operations, community engagement, and staff capabilities for sustained success.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-planning-items-wrapper"> {/* Kept existing wrapper class */}
                 {/* --- NEW PLANNING ITEMS (Mapped from second numbered block 1-7) --- */}
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBullhorn} /></div>
                    <div className="cliniqor-content"><h4>1. Marketing & Community Engagement</h4> <p>Combining traditional/digital strategies to build awareness, trust, and loyalty.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLaptop} /></div>
                    <div className="cliniqor-content"><h4>2. Custom Website Design</h4> <p>Creating intuitive, responsive sites to enhance patient access and engagement.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsers} /></div>
                    <div className="cliniqor-content"><h4>3. Public Relations & Community Connections</h4> <p>Fostering trust through strategic PR and CSR initiatives.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDatabase} /></div>
                    <div className="cliniqor-content"><h4>4. Advanced Hospital Information System (HIS)</h4> <p>Implementing robust HIS for streamlined operations and data management.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faFolderOpen} /></div>
                    <div className="cliniqor-content"><h4>5. Efficient Record Management & Data Security</h4> <p>Optimizing systems for data integrity, security, and accessibility.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <div className="cliniqor-content"><h4>6. Standard Operating Procedures (SOPs)</h4> <p>Creating tailored SOPs for consistency, quality, and efficiency.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up"> {/* Added 7th item */}
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                    <div className="cliniqor-content"><h4>7. Comprehensive Staff Training & Development</h4> <p>Upskilling teams to reduce errors, improve care, and stay current.</p> </div>
                 </div>
                 {/* --- END NEW PLANNING ITEMS --- */}
            </div>
          </div>
        </section>

        {/* --- NEW SECTION: METHODOLOGY --- */}
        <section className="cliniqor-section cliniqor-section-bg-light"> {/* Reusing a background style */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Cliniqor's Methodology</h2>
                 <p>Precision, Expertise, and Continuous Growth</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up"> {/* Simple text container */}
                   <p>At Cliniqor, we take a meticulous, results-driven approach to improving healthcare operations. Our methodology starts with detailed audits and data analysis, which we use to craft customized solutions that drive operational excellence and improve patient outcomes.</p>
                   <p>We believe in continuous improvement, constantly refining strategies to ensure your hospital remains at the forefront of healthcare delivery. We collaborate closely with your leadership team to implement changes, measure progress, and ensure that the solutions we provide are sustainable, scalable, and tailored to your specific needs.</p>
              </div>
           </div>
        </section>

        {/* --- NEW SECTION: RESULTS / SUPPORT --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title6Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title6Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Delivering Sustainable Results</h2>
                 <p>Tailored Expertise for Every Step of the Journey</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up"> {/* Simple text container */}
                   <p>Cliniqor’s team of seasoned consultants provides hands-on support, offering specialized resources to help implement strategies, improve workflows, and optimize operational efficiency. We assign the right professional to each task, ensuring that every step of the process is handled with the utmost care and expertise.</p>
                   <p>Whether you're seeking to optimize patient flow, reduce costs, improve staff productivity, or enhance patient care, Cliniqor ensures that our solutions deliver measurable, sustainable improvements. Our goal is not only to help you solve immediate challenges but to set your hospital on a path to long-term success, all while enhancing patient satisfaction and ensuring compliance with the highest industry standards.</p>
              </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- NEW FOOTER CONTENT --- */}
          <h3>Shape the Future of Healthcare with Cliniqor</h3>
          <p>Cliniqor’s hospital management consulting services empower healthcare institutions to navigate complexity, enhance patient outcomes, and achieve operational brilliance. Partner with us to shape the future of healthcare — one insight at a time.</p>
          {/* --- END NEW FOOTER CONTENT --- */}
          <a href="#contact" className="cliniqor-cta-button">Request a Consultation</a> {/* Button kept */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;